import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Membership } from '@/contracts/membership';
import { useAuthData } from '@/hooks/useAuthData';
import { generateDeviceFingerprint } from '@/adapters/security';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isEditor: boolean;
  isProMember: boolean;
  isVendor: boolean;
  membership: Membership | null;
  avatarUrl: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [isProMember, setIsProMember] = useState(false);
  const [isVendor, setIsVendor] = useState(false);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Sync auth data from cached hook to context state
  const { data: authData } = useAuthData(user?.id);

  useEffect(() => {
    if (authData) {
      const isAdminUser = authData.roles.some(r => r.role === 'admin');
      const isEditorUser = authData.roles.some(r => r.role === 'editor' || r.role === 'admin');
      setIsAdmin(isAdminUser);
      setIsEditor(isEditorUser);
      setIsProMember(authData.profile?.is_pro_member ?? false);
      setIsVendor(authData.profile?.is_vendor ?? false);
      setAvatarUrl(authData.profile?.avatar_url ?? null);
      setMembership(authData.membership);
    } else if (!user) {
      setIsAdmin(false);
      setIsEditor(false);
      setIsProMember(false);
      setIsVendor(false);
      setMembership(null);
      setAvatarUrl(null);
    }
  }, [authData, user]);

  useEffect(() => {
    supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Track security on sign in
      if (event === 'SIGNED_IN' && session?.user) {
        setTimeout(async () => {
          try {
            const device = generateDeviceFingerprint();
            const { data: deviceData } = await supabase.functions.invoke('track-security', {
              body: {
                action: 'track_device',
                data: {
                  device_fingerprint: device.fingerprint,
                  device_name: device.deviceName,
                },
              },
            });

            if (deviceData?.device) {
              await supabase.functions.invoke('track-security', {
                body: {
                  action: 'create_session',
                  data: { device_id: deviceData.device.id },
                },
              });
            }
          } catch (error) {
            console.error('Security tracking failed:', error);
          }
        }, 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Terminate session
    try {
      const { data: sessions } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', user?.id)
        .eq('is_active', true);
      
      if (sessions) {
        await supabase
          .from('user_sessions')
          .update({ is_active: false })
          .eq('user_id', user?.id);
      }
    } catch (error) {
      console.error('Failed to terminate session:', error);
    }

    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsEditor(false);
    setIsProMember(false);
    setIsVendor(false);
    setMembership(null);
    setAvatarUrl(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isEditor,
        isProMember,
        isVendor,
        membership,
        avatarUrl,
        loading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
